<template>
    <Dialog :visible="visible" @update:visible="$emit('update:visible', $event)" :modal="true"
            :header="isEdit ? 'Editar mesa' : 'Nueva mesa'" :style="{width: '460px'}"
            :breakpoints="{'640px': '94vw'}" class="p-fluid">
        <div class="field">
            <label for="rt-name">Nombre *</label>
            <InputText id="rt-name" v-model="form.name" maxlength="40" placeholder="Ej. Mesa 1, Barra 2" />
        </div>
        <div class="field">
            <label for="rt-zone">Zona</label>
            <Dropdown id="rt-zone" v-model="form.zoneId" :options="zones" optionLabel="name" optionValue="id"
                      placeholder="Sin zona" :showClear="true"
                      :emptyMessage="'Crea zonas con «Zonas» en Configurar salón'" />
            <small v-if="zoneChanged" class="rt-help">Se ubicará en un espacio libre de la nueva zona.</small>
        </div>
        <div class="field">
            <label>Forma</label>
            <SelectButton v-model="form.shape" :options="shapes" optionLabel="label" optionValue="value"
                          :unselectable="false" aria-label="Forma de la mesa" />
        </div>
        <div class="field">
            <label for="rt-capacity">Capacidad (personas)</label>
            <InputNumber id="rt-capacity" v-model="form.capacity" :min="0" :max="99" showButtons />
        </div>

        <template #footer>
            <div class="flex align-items-center justify-content-between">
                <Button v-if="isEdit" label="Eliminar" icon="pi pi-trash" class="p-button-text p-button-danger"
                        :loading="deleting" @click="confirmDelete" />
                <span v-else></span>
                <span>
                    <Button label="Cancelar" class="p-button-text" @click="$emit('update:visible', false)" />
                    <Button label="Guardar" icon="pi pi-check" :disabled="!canSave || saving" :loading="saving"
                            @click="save" />
                </span>
            </div>
        </template>
    </Dialog>
</template>

<script>
import httpRequest from '../../service/httpRequest';

const SHAPES = [
    { label: 'Cuadrada', value: 'square' },
    { label: 'Redonda', value: 'round' },
    { label: 'Rectangular', value: 'rect' },
];

export default {
    name: 'TableFormDialog',
    props: {
        visible: { type: Boolean, default: false },
        table: { type: Object, default: null },        // null = crear; objeto = editar
        zones: { type: Array, default: () => [] },     // zonas reales (entidad)
        defaultZoneId: { type: Number, default: null },// zona abierta en el salón al crear
    },
    emits: ['update:visible', 'saved', 'deleted'],
    data() {
        return {
            form: { id: null, name: '', zoneId: null, shape: 'square', capacity: null },
            shapes: SHAPES,
            saving: false,
            deleting: false,
        };
    },
    computed: {
        isEdit() { return !!(this.table && this.table.id); },
        canSave() { return !!(this.form.name && this.form.name.trim()); },
        zoneChanged() { return this.isEdit && (this.form.zoneId || null) !== (this.table.zoneId || null); },
    },
    watch: {
        visible(open) {
            if (!open) return;
            if (this.table) {
                this.form = {
                    id: this.table.id, name: this.table.name || '', zoneId: this.table.zoneId || null,
                    shape: this.table.shape || 'square', capacity: this.table.capacity || null,
                };
            } else {
                this.form = { id: null, name: '', zoneId: this.defaultZoneId, shape: 'square', capacity: null };
            }
        },
    },
    methods: {
        async save() {
            this.saving = true;
            // Sin posX/posY: el servidor conserva la posición al editar y ubica sola la mesa nueva
            // (o la que cambia de zona) en un hueco libre de su plano.
            const payload = {
                name: this.form.name.trim(),
                zoneId: this.form.zoneId || null,
                shape: this.form.shape || 'square',
                capacity: this.form.capacity || null,
            };
            if (this.form.id) payload.id = this.form.id;
            const res = await httpRequest.saveRestaurantTable(payload);
            this.saving = false;
            if (res && res.status === 200) {
                this.$toast.add({ severity: 'success', summary: 'Mesa guardada', life: 2500 });
                this.$emit('saved', res.data);
                this.$emit('update:visible', false);
            } else {
                this.$toast.add({ severity: 'warn', summary: 'No se pudo guardar',
                                  detail: (res && res.error) || 'Intenta de nuevo.', life: 4000 });
            }
        },
        confirmDelete() {
            this.$confirm.require({
                message: `¿Eliminar «${this.form.name}»? Solo se puede si no tiene cuentas abiertas.`,
                header: 'Eliminar mesa', icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Eliminar', rejectLabel: 'Cancelar', acceptClass: 'p-button-danger',
                accept: () => this.doDelete(),
            });
        },
        async doDelete() {
            this.deleting = true;
            const res = await httpRequest.deleteRestaurantTable(this.form.id);
            this.deleting = false;
            if (res && res.status === 200) {
                this.$toast.add({ severity: 'success', summary: 'Mesa eliminada', life: 2500 });
                this.$emit('deleted', this.form.id);
                this.$emit('update:visible', false);
            } else {
                this.$toast.add({ severity: 'warn', summary: 'No se pudo eliminar',
                                  detail: (res && res.error) || 'Intenta de nuevo.', life: 4000 });
            }
        },
    },
};
</script>

<style scoped>
.rt-help { color: var(--text-color-secondary); }
</style>
